import type { Intervention } from '@seen/core/models/care-plans/intervention'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { TypographyDetail } from '@seen/ui'
import type { JSX } from 'react'
import { Activity } from '#src/components/activity/Activity.tsx'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import {
  buildInterventionActivity,
  NOTE_LIMIT,
} from '#src/components/care-plan/interventions/buildInterventionActivity.ts'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { useCreatedBy } from '#src/hooks/useCreatedBy.ts'
import { getParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

/* One feed for everything that has happened to an intervention: the comments
 * written on it, the signed notes that mention it, and the fact of its
 * creation. Built like `CompositionActivity` and `AppointmentActivity` —
 * heterogeneous items merged into one array, ordered newest first, rendered
 * through `ActivityEntry`/`ActivityComment` inside a single `ActivityList` so
 * they share one avatar spine.
 *
 * The notes half reads a ONE-WAY link: a note records the interventions it
 * mentions in its own `section.entry` when it is signed, and nothing is written
 * back onto the intervention. */
export function InterventionActivity({
  intervention,
}: {
  intervention: Intervention
}): JSX.Element {
  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([intervention.reference])
  )

  const { data: notes = [] } = useSearch(
    ParticipantNote,
    ParticipantNote.querySignedByEntry(
      intervention.reference.reference,
      intervention.patientSubject,
      NOTE_LIMIT
    ),
    /* The sheet opens over a chart the clinician is already reading; a note
     * they cannot reach should quietly not be listed rather than raise a toast
     * over a lookup they never asked for. */
    { silentErrors: true }
  )

  /* Who created it, which is the oldest version's author — not `author`, which
   * this product calls the Owner and which changes hands. */
  const { data: createdBy } = useCreatedBy(intervention.reference)

  const entries = buildInterventionActivity({
    comments,
    notes,
    created: intervention.created,
  })

  /* The shared wrapper supplies the heading, the comment composer and the
   * `ActivityList` spine — the same one the composition, appointment and SDR
   * feeds use. It renders no subscribe control here because a `CarePlan` is not
   * `Subscribeable`.
   *
   * Keys sit on the entries themselves rather than on a wrapper element:
   * `ActivityEntry` uses `group-last:` to stop the list's vertical line
   * emanating past the final entry, which needs it to be the last child. */
  return (
    <Activity resource={intervention} subject={intervention.patientSubject}>
      {entries.map((entry) => {
        if (entry.kind === 'comment') {
          return (
            <ActivityComment
              key={entry.comment.reference.reference}
              comment={entry.comment}
            />
          )
        }
        if (entry.kind === 'note') {
          return (
            /* Title inline in the action rather than as `children`, which
             * `ActivityEntry` would drop onto a second line: the same
             * one-line "<actor> <verb> <TextLink title>" shape the participant
             * timeline uses for a note. */
            <ActivityEntry
              key={entry.note.reference.reference}
              author={entry.note.signerReference}
              time={entry.time}
              action={
                <>
                  added related note{' '}
                  <TextLink to={entry.href}>
                    {getParticipantNoteTitle(entry.note)}
                  </TextLink>
                </>
              }
            />
          )
        }
        if (entry.kind === 'notes-truncated') {
          /* Not an `ActivityEntry`: it is a statement about the list, not
           * something that happened, so it carries no avatar and no time.
           * Indented to the depth `ActivityEntry` gives its own children so it
           * reads as part of the feed rather than as stray text. */
          return (
            <TypographyDetail
              key="notes-truncated"
              className="ml-9.5 text-muted-foreground"
            >
              Showing the {entry.shown} most recent notes. Older notes
              mentioning this intervention are not shown.
            </TypographyDetail>
          )
        }
        return (
          <ActivityEntry
            key="created"
            author={createdBy}
            time={entry.time}
            action="created this intervention"
          />
        )
      })}
    </Activity>
  )
}
